import { useState } from "react";
import { GuestProfileName } from "components/GuestProfileDrawer";
import { Checkbox } from "@mui/material";
import useCommon from "hooks/useCommon";
import usePermissions from "hooks/usePermissions";
import { toggleArrivalChecklist } from "store/slice/arrivalsSlice";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiBadge from "components/VuiBadge";
import VuiButton from "components/VuiButton";

import colors from "assets/theme/base/colors";
import borders from "assets/theme/base/borders";
import rgba from "assets/theme/functions/rgba";

import useHotelDateTime from "hooks/useHotelDateTime";

const { dark, primary, light } = colors;
const { borderWidth } = borders;

const PrepChecklist = ({ items = [], disabled, saving, onToggle }) => {
  const DEFAULT_VISIBLE = 3;
  const [expanded, setExpanded] = useState(false);

  const hasMore = items.length > DEFAULT_VISIBLE;

  const visibleItems = expanded || !hasMore ? items : items.slice(0, DEFAULT_VISIBLE);

  return (
    <VuiBox>
      <VuiTypography
        variant="caption"
        sx={{
          opacity: 0.6,
          fontSize: "11px",
          letterSpacing: "0.3px",
          mb: 0.5,
        }}
      >
        Room Preparation
      </VuiTypography>

      <VuiBox display="flex" flexDirection="column" gap={0.4}>
        {visibleItems.map((item) => (
          <VuiBox key={item.id} component="label" display="flex" alignItems="center" gap={0.8}
            onClick={(event) => event.stopPropagation()}
            sx={{ cursor: disabled ? "default" : "pointer" }}>
            <Checkbox
              checked={Boolean(item.done)}
              disabled={disabled}
              onChange={(event) => onToggle(item.id, event.target.checked)}
              inputProps={{ "aria-label": item.title, "aria-busy": saving }}
              size="small"
              sx={{ p: 0, "& .MuiSvgIcon-root": { fontSize: 18 } }}
            />

            <VuiTypography
              variant="caption"
              sx={{
                color: primary.main,
                textDecoration: item.done ? "line-through" : "none",
                fontSize: "12px",
              }}
            >
              {item.title}
            </VuiTypography>
          </VuiBox>
        ))}

        {hasMore && (
          <VuiTypography
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
            sx={{
              color: primary.main,
              fontSize: "12px",
              fontWeight: 600,
              cursor: "pointer",
              mt: 0.4,
              ml: "20px",
              width: "fit-content",
              "&:hover": { opacity: 0.8 },
            }}
          >
            {expanded ? "Show less" : `Show ${items.length - 3} more`}
          </VuiTypography>
        )}
      </VuiBox>
    </VuiBox>
  );
};

const GuestAISummary = ({ summary }) => {
  const LIMIT = 210;
  const [expanded, setExpanded] = useState(false);

  const isLong = summary?.length > LIMIT;

  const shortText = isLong && !expanded ? summary.slice(0, LIMIT).trim() : summary;

  return (
    <VuiBox
      mt={1}
      p={1}
      borderRadius="lg"
      sx={{
        background: light.main,
        border: `${borderWidth[1]} solid ${rgba(dark.main, 0.25)}`,

        minHeight: expanded ? "auto" : "100px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
      }}
    >
      <VuiTypography
        variant="caption"
        fontWeight="medium"
        sx={{
          fontSize: "11px",
          letterSpacing: "0.3px",
          color: primary.main,
          display: "flex",
          alignItems: "center",
          gap: "4px",
          mb: 0.8,
        }}
      >
        AI Summary
      </VuiTypography>

      <VuiTypography
        variant="body2"
        sx={{
          fontSize: "12px",
          lineHeight: 1.5,
          opacity: 0.9,
          whiteSpace: "pre-line",
        }}
      >
        {shortText}

        {isLong && !expanded && "... "}

        {isLong && (
          <VuiTypography
            component="span"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
            sx={{
              color: primary.main,
              fontSize: "12px",
              fontWeight: 600,
              cursor: "pointer",
              display: "inline",
              "&:hover": { opacity: 0.8 },
            }}
          >
            {expanded ? " Read less" : "Read more"}
          </VuiTypography>
        )}
      </VuiTypography>
    </VuiBox>
  );
};

const ArrivalCard = ({ data, onView, onPreferences, isDeparture = false }) => {
  const { getHotelDateTime } = useHotelDateTime();
  const { DISPATCH, STORE } = useCommon();
  const { can } = usePermissions();
  const saving = STORE((state) => Boolean(state.arrivals.checklistSaving[data?._id]));
  const [checklistError, setChecklistError] = useState("");
  const [viewOnly, setViewOnly] = useState(false);

  if (!data) return null;

  const canEditChecklist = !isDeparture && can("editArrivalDeparture") && !viewOnly;

  const handleToggleChecklist = async (itemId, isChecked) => {
    if (!canEditChecklist || saving) return;
    setChecklistError("");
    try {
      await DISPATCH(toggleArrivalChecklist({ reservationId: data._id, itemId, isChecked })).unwrap();
    } catch (error) {
      if (error?.name === "ConditionError") return;
      if (error?.status === 409) setViewOnly(true);
      setChecklistError(error?.message || "Unable to update checklist item. Please try again.");
    }
  };

  const arrivalTime = getHotelDateTime(data.datetime);

  const formattedDate = arrivalTime.format("MMM D");
  const formattedTime = arrivalTime.format("h:mm A");

  const checklistItems = (data.prepChecklistItems || []).filter((i) => !i.isDeleted);

  return (
    <VuiBox
      p={1.5}
      borderRadius="xl"
      onClick={onView}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",

        background: "rgba(255,255,255,0.03)",
        backdropFilter: "blur(20px)",
        border: "1px solid #1A1D1A",
        transition: "all 0.25s ease",
        cursor: "pointer",
        "&:hover": {
          transform: "translateY(-4px)",
        },
      }}
    >
      {/*  ROOM + DATE */}
      <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
        <VuiTypography variant="h5" fontWeight="normal">
          {data.roomNumber || "—"}
        </VuiTypography>

        <VuiTypography variant="caption" sx={{ opacity: 0.8, fontSize: "12px" }}>
          {formattedDate} • {formattedTime}
        </VuiTypography>
      </VuiBox>

      {/*  GUEST + OCCASION */}
      <VuiBox mb={0}>
        <VuiTypography
          variant="caption"
          sx={{
            opacity: 0.6,
            fontSize: "11px",
            letterSpacing: "0.4px",
            mb: 0.5,
          }}
        >
          Guest
        </VuiTypography>

        <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={0}>
          <VuiTypography variant="h6" fontWeight="light" sx={{ letterSpacing: "0.2px" }}>
            <GuestProfileName guestId={data.guestId} hotelId={data.hotelId} name={data.guestName} />
          </VuiTypography>

          {data.occasion && (
            <VuiBadge variant="gradient" color="success" size="sm" badgeContent={data.occasion} />
          )}
        </VuiBox>
        {data.aiSummary?.summaryText && !data.aiSummary?.isDeleted && (
          <GuestAISummary summary={data.aiSummary.summaryText} />
        )}
      </VuiBox>

      {/*  REQUESTS */}
      {!isDeparture && data.requests && (
        <VuiBox mt={1}>
          <VuiTypography
            variant="caption"
            sx={{ opacity: 0.6, fontSize: "11px", letterSpacing: "0.3px" }}
          >
            Special Request
          </VuiTypography>

          <VuiTypography
            variant="body2"
            sx={{
              lineHeight: 1.5,
              opacity: 0.9,
              fontSize: "14px",
            }}
          >
            {data.requests}
          </VuiTypography>
        </VuiBox>
      )}

      {/*  PREP CHECKLIST */}
      <VuiBox mb={2}>
        {!isDeparture && checklistItems?.length > 0 && (
          <PrepChecklist
            disabled={!canEditChecklist || saving}
            saving={saving}
            onToggle={handleToggleChecklist}
            items={checklistItems.map((i) => ({
              id: i._id,
              title: i.title,
              done: i.isChecked,
            }))}
          />
        )}
        {saving && (
          <VuiTypography variant="caption" role="status">Saving checklist...</VuiTypography>
        )}
        {checklistError && (
          <VuiTypography variant="caption" color="error" role="alert">
            {checklistError}
          </VuiTypography>
        )}
      </VuiBox>

      <VuiBox display="flex" gap={1} mt="auto">
        <VuiButton
          size="small"
          variant="outlined"
          color="primary"
          fullWidth
          onClick={(e) => {
            e.stopPropagation();
            onView();
          }}
        >
          Details
        </VuiButton>

        {!isDeparture && (
          <VuiButton
            size="small"
            variant="outlined"
            color="primary"
            fullWidth
            onClick={(e) => {
              e.stopPropagation();
              onPreferences();
            }}
          >
            Preferences
          </VuiButton>
        )}
      </VuiBox>
    </VuiBox>
  );
};

export default ArrivalCard;
